"use client";

import { usePathname } from "next/navigation";
import dynamic from "next/dynamic";
import { useEffect, useState } from "react";

const FloatingChatbotWidget = dynamic(
	() =>
		import("@/components/ui/FloatingChatbotWidget").then(
			(m) => m.FloatingChatbotWidget,
		),
	{ ssr: false },
);

export function ClientChatbotWrapper() {
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		// Delay loading chatbot widget until after the main thread is idle or on first interaction
		const triggerMount = () => {
			setMounted(true);
			cleanup();
		};

		const cleanup = () => {
			window.removeEventListener("scroll", triggerMount);
			window.removeEventListener("touchstart", triggerMount);
			window.removeEventListener("click", triggerMount);
		};

		window.addEventListener("scroll", triggerMount, {
			passive: true,
			once: true,
		});
		window.addEventListener("touchstart", triggerMount, {
			passive: true,
			once: true,
		});
		window.addEventListener("click", triggerMount, {
			passive: true,
			once: true,
		});

		let idleId: any;
		if ("requestIdleCallback" in window) {
			idleId = (window as any).requestIdleCallback(triggerMount, {
				timeout: 6000,
			});
		} else {
			idleId = setTimeout(triggerMount, 4000);
		}

		return () => {
			cleanup();
			if ("cancelIdleCallback" in window && typeof idleId === "number") {
				(window as any).cancelIdleCallback(idleId);
			} else {
				clearTimeout(idleId);
			}
		};
	}, []);

	const pathname = usePathname();
	if (!mounted) return null;

	// paths where chatbot should not show
	const hiddenPaths = [
		"coming-soon",
		"verify-handler",
		"/email-verify",
		"/about-us",
		"/auth",
		"/studio",
		"/chat",
		"/ai-course-finder/eligibility",
		"/ai-course-finder/program",
		"/university-dashboard",
		"/student-profile",
		"/scholarship-test",
		"/tools/check-eligibility",
		"/careers",
		"/llb-llm-programs",
		"/study-abroad-apply",
	];

	const hiddenDynamicPaths = ["/careers/"];

	const isHidden =
		hiddenPaths.includes(pathname) ||
		hiddenDynamicPaths.some((path) => pathname.startsWith(path));

	// hide on exact match
	if (hiddenPaths.includes(pathname)) return null;

	if (pathname.startsWith("/studio")) return null;
	if (pathname.startsWith("/study")) return null;

	if (
		pathname.startsWith("/ai-course-finder/eligibility") ||
		pathname.startsWith("/ai-course-finder/program")
	) {
		return null;
	}
	if (pathname.startsWith("/scholarship-test")) return null;

	if (isHidden) return null;
	return <FloatingChatbotWidget />;
}
